import React, { useEffect, useState, useMemo } from 'react';
import { ArrowUp, ArrowDown } from "react-feather";
import { useApi } from './services/api';
import './App.css';

export interface TickerInfo {
  symbol: string;
  currentPrice: number;
  change?: number;
  percentChange?: number;
  highPrice?: number;
  lowPrice?: number;
  openPrice?: number;
  previousClosePrice: number;
  timestamp: number;
}

const Ticker: React.FC<{ ticker: TickerInfo }> = ({ ticker }) => {
  const percentChanged = ticker.percentChange ?? 
    ((ticker.currentPrice - ticker.previousClosePrice) / ticker.previousClosePrice) * 100;

  return (
    <div className="ticker-item">
      <span className="ticker-symbol">{ticker.symbol}</span>
      <span className="ticker-price">${ticker.currentPrice.toFixed(2)}</span>
      <span className={`ticker-change ${percentChanged > 0 ? 'positive' : 'negative'}`}>
        {percentChanged > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        {Math.abs(percentChanged).toFixed(2)}%
      </span>
    </div>
  );
};

const WatchList: React.FC = () => {
  const symbols = useMemo(() => ["GOOG", "VZ", "AA", "META", "BITB", "NVDA", "JPM", "MSFT", "RIO", "GDX", "AAPL"], []);
  const [tickerData, setTickerData] = useState<TickerInfo[]>([]);
  const [currentSymbolIndex, setCurrentSymbolIndex] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [allSymbolsLoaded, setAllSymbolsLoaded] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const currentSymbol = symbols[currentSymbolIndex];
  const api = useApi<TickerInfo>(`MarketData/Quote/${currentSymbol}`, 'GET');

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await api();
        if (response.error) throw new Error(response.error.message);
        if (response.data) {
          setTickerData(prevData => {
            const newData = [...prevData];
            const index = newData.findIndex(t => t.symbol === currentSymbol);
            if (index !== -1) newData[index] = response.data as TickerInfo;
            else newData.push(response.data as TickerInfo);
            return newData;
          });
        }
      } catch (error) {
        console.error('Error fetching ticker data:', error);
        setApiError(`Error fetching data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    fetchQuote();
  }, [api, currentSymbol]);

  useEffect(() => {
    if (tickerData.length === symbols.length) {
      setAllSymbolsLoaded(true);
    }
  }, [tickerData, symbols]);

  useEffect(() => {
    const cycleThroughSymbols = () => {
      if (currentSymbolIndex === symbols.length - 1) {
        setIsWaiting(true);
        setTimeout(() => {
          setIsWaiting(false);
          setCurrentSymbolIndex(0);
        }, 900000);
      } else {
        setCurrentSymbolIndex(prevIndex => prevIndex + 1);
      }
    };

    const symbolInterval = setInterval(() => {
      if (!isWaiting) {
        cycleThroughSymbols();
      }
    }, 400);

    return () => clearInterval(symbolInterval);
  }, [symbols.length, currentSymbolIndex, isWaiting]);

  if (apiError) {
    return <div className="error-message">API Error: {apiError}</div>;
  }

  if (!allSymbolsLoaded) {
    return <div className="loading-message">Loading ticker data...</div>;
  }

  return (
    <div className={`ticker-container ${allSymbolsLoaded ? 'loaded' : ''}`}>
      <div className="ticker-wrapper">
        {tickerData.map((ticker) => (
          <Ticker key={ticker.symbol} ticker={ticker} />
        ))}
        {tickerData.map((ticker) => (
          <Ticker key={`${ticker.symbol}-duplicate`} ticker={ticker} />
        ))}
      </div>
    </div>
  );
};

export default WatchList;
